import {
  DAY_NAMES,
  type AutomationPlatform,
  type DayOfWeek,
} from '@backend/lib/db/schema/automation'
import type { AutomationItem } from './constants'

/**
 * A logical automation shown in the UI. The DB stores one row per (source,
 * platforms, sendReminders, day); the frontend groups rows that share
 * everything-but-the-day into one card with multiple day badges.
 */
export interface AutomationGroup {
  key: string
  rows: AutomationItem[]
  days: DayOfWeek[]
  representative: AutomationItem
  moodboardId: number
  platforms: AutomationPlatform[]
  sendReminders: boolean
}

/** The fields that decide whether two rows collapse into one card. */
export type GroupKeyInput = Pick<AutomationItem, 'moodboardId' | 'platforms' | 'sendReminders'>

/** Rows sharing this key are the same logical automation on different days. */
export function groupKey(a: GroupKeyInput): string {
  const platforms = [...a.platforms].sort().join(',')
  return `m${a.moodboardId}|${platforms}|${a.sendReminders}`
}

const dayIndex = (d: DayOfWeek) => DAY_NAMES.indexOf(d)

function createdAtMs(a: AutomationItem): number {
  return a.createdAt ? new Date(a.createdAt).getTime() : 0
}

export function groupAutomations(automations: AutomationItem[]): AutomationGroup[] {
  const buckets = new Map<string, AutomationItem[]>()
  for (const a of automations) {
    const key = groupKey(a)
    const bucket = buckets.get(key)
    if (bucket) bucket.push(a)
    else buckets.set(key, [a])
  }

  const groups: AutomationGroup[] = []
  for (const [key, rows] of buckets) {
    // Stable representative: earliest createdAt (ties broken by id).
    const representative = [...rows].sort(
      (x, y) => createdAtMs(x) - createdAtMs(y) || x.id - y.id,
    )[0]!
    // Unique days, in DAY_NAMES order (two rows can share a day — list once).
    const days = [...new Set(rows.map((r) => r.dayOfWeek))].sort(
      (a, b) => dayIndex(a) - dayIndex(b),
    )
    groups.push({
      key,
      rows,
      days,
      representative,
      moodboardId: representative.moodboardId,
      platforms: representative.platforms,
      sendReminders: representative.sendReminders,
    })
  }

  // Sort by first day, then representative createdAt for stability.
  return groups.sort(
    (a, b) =>
      dayIndex(a.days[0] ?? 'monday') - dayIndex(b.days[0] ?? 'monday') ||
      createdAtMs(a.representative) - createdAtMs(b.representative),
  )
}

/** day → rows landing on that day. Drives the edit reconcile (dupes preserved). */
export function dayRowMap(group: AutomationGroup): Map<DayOfWeek, AutomationItem[]> {
  const map = new Map<DayOfWeek, AutomationItem[]>()
  for (const row of group.rows) {
    const list = map.get(row.dayOfWeek)
    if (list) list.push(row)
    else map.set(row.dayOfWeek, [row])
  }
  return map
}
